import { useOutletContext, Link } from "react-router-dom";
import SEO from "../components/SEO";
import DestinationCard from "../components/DestinationCard";
import Reveal from "../components/Reveal";
import { t } from "../i18n";
import { cities, airports, countries, staticPath, hubs, hubPath } from "../data";

export default function ListingPage({ kind }) {
  const { lang } = useOutletContext();
  const i = t[lang];
  const isAirports = kind === "airports";
  const items = isAirports ? airports : cities;

  const title = isAirports
    ? lang === "it"
      ? "Noleggio auto in aeroporto — I principali aeroporti | BoingCars"
      : "Airport Car Rental — Major Airports Worldwide | BoingCars"
    : lang === "it"
      ? "Destinazioni noleggio auto — Città in tutto il mondo | BoingCars"
      : "Car Rental Destinations — Cities Worldwide | BoingCars";
  const desc = isAirports
    ? lang === "it"
      ? "Confronta il noleggio auto nei principali aeroporti del mondo: ritiro al terminal, centri noleggio e consigli di guida locali."
      : "Compare car rental at the world's major airports: terminal pick-up, rental centres and local driving advice."
    : lang === "it"
      ? "Esplora le destinazioni di noleggio auto BoingCars: guide locali, punti di ritiro e ricerca live per ogni città."
      : "Explore BoingCars car rental destinations: local guides, pick-up locations and live search for every city.";

  return (
    <>
      <SEO
        lang={lang}
        title={title}
        description={desc}
        pathEn={staticPath("en", kind)}
        pathIt={staticPath("it", kind)}
      />
      <section className="container-site py-16 md:py-24" data-testid={`${kind}-listing`}>
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-widest text-ember">
            {isAirports ? i.nav.airports : i.nav.destinations}
          </p>
          <h1 className="mt-3 max-w-2xl font-display text-4xl font-extrabold tracking-tighter sm:text-5xl">
            {isAirports ? i.home.airTitle : i.home.destTitle}
          </h1>
          <p className="mt-4 max-w-lg text-base text-muted-foreground md:text-lg">
            {isAirports ? i.home.airSub : i.home.destSub}
          </p>
        </Reveal>

        {!isAirports ? (
          Object.entries(countries).map(([key, country]) => {
            const list = items.filter((p) => p.country === key);
            if (!list.length) return null;
            return (
              <div key={key} className="mt-14">
                <Reveal>
                  <h2 className="border-b border-border pb-3 font-display text-xl font-bold tracking-tight text-petrol md:text-2xl">
                    {country.name[lang]}
                    {key === "italy" && (
                      <Link
                        to={hubPath(lang, hubs.italia)}
                        data-testid="listing-hub-italy"
                        className="ml-3 align-middle text-xs font-semibold text-ember transition-colors duration-200 hover:text-petrol"
                      >
                        {lang === "it" ? "Vedi hub Italia →" : "View Italy hub →"}
                      </Link>
                    )}
                  </h2>
                </Reveal>
                <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
                  {list.map((p, idx) => (
                    <Reveal key={p.id} delay={(idx % 4) * 0.05}>
                      <DestinationCard page={p} lang={lang} />
                    </Reveal>
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((p, idx) => (
              <Reveal key={p.id} delay={(idx % 3) * 0.05}>
                <DestinationCard page={p} lang={lang} />
              </Reveal>
            ))}
          </div>
        )}

        <Reveal className="mt-14 rounded-2xl bg-white p-6 text-sm text-muted-foreground ring-1 ring-black/5">
          {i.common.affiliateNote}
        </Reveal>
        {isAirports && (
          <Reveal className="mt-6">
            <Link to={staticPath(lang, "carRental")} data-testid="back-to-destinations" className="text-sm font-semibold text-petrol hover:text-petrol-light">
              ← {i.nav.destinations}
            </Link>
          </Reveal>
        )}
      </section>
    </>
  );
}
